/**
 * Typed API client. Wraps fetch with the user's Cognito access token.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  MeResponse, ApiQuestion, AttemptInput, AttemptResult,
  LeaderboardResponse, LeaderboardScope, GroupedLeaderboardResponse, IngestionRequest, IngestionJob, StudyMaterial,
  School, ClassCode, Professor, ClassSection, GeneralExam, GeneralExamSection,
  CheckoutRequest, CheckoutResponse, SubscriptionInfo, ApiDeck, ClassComment, AppConfigResponse,
  BillingConfigResponse, UploadSource,
} from '@shared/types';

const API_URL = import.meta.env.VITE_API_URL ?? '/api';

const SESSION_TOKEN_KEY = 'squishy_session_token';

export const APP_BUILD_ID = import.meta.env.VITE_APP_BUILD_ID ?? 'dev';

async function publicFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  const res = await fetch(`${API_URL}${path}`, { ...init, headers, cache: 'no-store' });
  if (!res.ok) throw new Error(`Something went wrong (${res.status})`);
  return res.json() as Promise<T>;
}

export async function authedFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  // Attach session token for single-session enforcement
  const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
  if (sessionToken) headers.set('x-session-token', sessionToken);

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text();
    let friendly = '';
    let errorCode = '';
    try {
      const parsed = JSON.parse(text);
      errorCode = parsed.error ?? '';
      if (parsed.message) {
        try {
          const zodErrors = JSON.parse(parsed.message);
          if (Array.isArray(zodErrors)) {
            friendly = zodErrors.map((e: any) => e.message).join('. ');
          }
        } catch {
          friendly = parsed.message;
        }
      } else if (parsed.error) {
        friendly = parsed.error;
      }
    } catch { /* not JSON */ }

    // Handle session invalidation — sign out and redirect to login
    if (errorCode === 'session_invalidated') {
      localStorage.removeItem(SESSION_TOKEN_KEY);
      const { signOut } = await import('aws-amplify/auth');
      await signOut().catch(() => {});
      window.location.href = '/login?reason=session_expired';
      throw new Error('Session expired — signed in on another device.');
    }

    throw new Error(friendly || `Something went wrong (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Request permanent deletion of the account tied to this email. */
export async function requestAccountDeletion(email: string, reason?: string) {
  return publicFetch<{ received: boolean }>('/account/delete-request', {
    method: 'POST',
    body: JSON.stringify({ email, reason: reason ?? '' }),
  });
}

const REFERRAL_KEY = 'squishy_pending_referral';

/** Call after Cognito login to establish a session token. */
export async function establishSession(): Promise<string> {
  // Pass any pending referral for first-time user attribution
  const referral = localStorage.getItem(REFERRAL_KEY);
  const body: Record<string, string> = {};
  if (referral) body.referral_share_id = referral;

  const { sessionToken } = await authedFetch<{ sessionToken: string }>('/auth/session', {
    method: 'POST', body: JSON.stringify(body),
  });
  localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
  // Clear referral after successful session (attribution handled server-side)
  if (referral) localStorage.removeItem(REFERRAL_KEY);
  return sessionToken;
}

export const useAppConfig = () => useQuery({
  queryKey: ['app-config'],
  queryFn: () => publicFetch<AppConfigResponse>('/app-config'),
  refetchInterval: 30_000,
  refetchOnWindowFocus: true,
  refetchIntervalInBackground: true,
  staleTime: 0,
});

// ── Profile ──────────────────────────────────────────────────────────────────

export const useMe = () => useQuery({
  queryKey: ['me'],
  queryFn: () => authedFetch<MeResponse>('/me'),
});

export async function recordPresence(input: {
  route: string;
  platform: string;
  visibilityState: 'visible' | 'hidden';
  contextType?: 'exam_section' | 'exam' | 'class' | 'none';
  contextId?: string | null;
}) {
  return authedFetch<{ ok: boolean }>('/me/presence', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export const useContextXp = (contextId: string | null) => useQuery({
  queryKey: ['context-xp', contextId],
  queryFn: () => authedFetch<{ xp: number }>(`/me/context-xp/${contextId}`),
  enabled: Boolean(contextId),
});

export const useUpdateProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<MeResponse['user']>) =>
      authedFetch<MeResponse>('/me', { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
};

// ── Taxonomy ─────────────────────────────────────────────────────────────────

export const useSchoolSearch = (q: string) => useQuery({
  queryKey: ['schools', q],
  queryFn: () => authedFetch<{ schools: School[] }>(`/taxonomy/schools?q=${encodeURIComponent(q)}`),
  enabled: q.length >= 2,
});

export const useClassSearch = (schoolId: string | null, q: string) => useQuery({
  queryKey: ['classes', schoolId, q],
  queryFn: () => authedFetch<{ classes: ClassCode[] }>(`/taxonomy/classes?school_id=${schoolId}&q=${encodeURIComponent(q)}`),
  enabled: Boolean(schoolId),
});

export const useProfessorSearch = (schoolId: string | null, q: string) => useQuery({
  queryKey: ['professors', schoolId, q],
  queryFn: () => authedFetch<{ professors: Professor[] }>(`/taxonomy/professors?school_id=${schoolId}&q=${encodeURIComponent(q)}`),
  enabled: Boolean(schoolId) && q.length >= 1,
});

export const useEnrollments = () => useQuery({
  queryKey: ['enrollments'],
  queryFn: () => authedFetch<{ enrollments: any[] }>('/taxonomy/enrollments'),
});

// ── Exams ────────────────────────────────────────────────────────────────────

export const useGeneralExams = () => useQuery({
  queryKey: ['exams'],
  queryFn: () => authedFetch<{ exams: GeneralExam[] }>('/exams'),
});

export const useExamSectionLookup = (sectionId: string | null) => useQuery({
  queryKey: ['exam-section-lookup', sectionId],
  queryFn: () => authedFetch<{ section: GeneralExamSection | null; exam: GeneralExam | null }>(`/exams/section-lookup/${sectionId}`),
  enabled: Boolean(sectionId),
});

export const useExamSections = (examId: string | null) => useQuery({
  queryKey: ['exam-sections', examId],
  queryFn: () => authedFetch<{ sections: GeneralExamSection[] }>(`/exams/${examId}/sections`),
  enabled: Boolean(examId),
});

export interface ExamSectionStat {
  sectionId: string;
  sectionCode: string;
  sectionName: string;
  totalQuestions: number;
  attempted: number;
  correctFirstTry: number;
  correctTotal: number;
  totalAttempts: number;
  accuracy: number;
}

export interface ExamStatsResponse {
  stats: {
    sections: ExamSectionStat[];
    totals: { attempted: number; correct: number; total: number; accuracy: number };
  };
}

export const useExamStats = (examId: string | null) => useQuery({
  queryKey: ['exam-stats', examId],
  queryFn: () => authedFetch<ExamStatsResponse>(`/exams/${examId}/stats`),
  enabled: Boolean(examId),
});

// ── Study Sessions ──────────────────────────────────────────────────────────

export interface StudySession {
  id: string;
  userId: string;
  examSectionId: string | null;
  classSectionId: string | null;
  filters: { topics: string[]; mode?: 'flashcard' | 'quick'; uploadIds?: string[] } | null;
  questionIds: string[];
  currentIndex: number;
  totalQuestions: number;
  correctCount: number;
  questionsAnswered: number;
  xpEarned: number;
  label: string | null;
  startedAt: string;
  endedAt: string | null;
  answers?: Record<string, { selectedAnswers: number[]; correct: boolean }>;
}

export const useSession = (sessionId: string | null) => useQuery({
  queryKey: ['session', sessionId],
  queryFn: () => authedFetch<{ session: StudySession }>(`/sessions/${sessionId}`),
  enabled: Boolean(sessionId),
  refetchOnMount: 'always',
});

export const useActiveSessions = (params: { examId?: string | null; classSectionId?: string | null }) => {
  const key = params.examId ?? params.classSectionId ?? null;
  const qs = params.examId ? `exam_id=${params.examId}` : params.classSectionId ? `class_section_id=${params.classSectionId}` : '';
  return useQuery({
    queryKey: ['sessions', key],
    queryFn: () => authedFetch<{ sessions: StudySession[] }>(`/sessions?${qs}`),
    enabled: Boolean(key),
  });
};

export const useAllActiveSessions = () => useQuery({
  queryKey: ['sessions', 'all'],
  queryFn: () => authedFetch<{ sessions: StudySession[] }>('/sessions?all=1'),
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
});

export const useCreateSession = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      examSectionId?: string;
      classSectionId?: string;
      filters?: { topics: string[]; mode?: 'flashcard' | 'quick'; uploadIds?: string[] } | null;
      questionIds: string[];
      label?: string;
    }) => authedFetch<{ session: StudySession }>('/sessions', {
      method: 'POST', body: JSON.stringify(body),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
};

export const useUpdateSession = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; currentIndex?: number; questionsAnswered?: number; correctCount?: number; xpEarned?: number }) =>
      authedFetch<{ session: StudySession }>(`/sessions/${id}`, {
        method: 'PATCH', body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
};

export const useCompleteSession = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authedFetch<{ session: StudySession }>(`/sessions/${id}/complete`, { method: 'POST', body: '{}' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
};

export const useSessionHistory = (params: { examId?: string | null; classSectionId?: string | null }) => {
  const qs = params.examId ? `exam_id=${params.examId}` : params.classSectionId ? `class_section_id=${params.classSectionId}` : '';
  return useQuery({
    queryKey: ['session-history', params.examId ?? params.classSectionId ?? 'all'],
    queryFn: () => authedFetch<{ sessions: StudySession[] }>(`/sessions/history?${qs}`),
    enabled: Boolean(params.examId || params.classSectionId),
  });
};

export const useDeleteSession = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authedFetch(`/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
};

// ── Questions / Study ────────────────────────────────────────────────────────

export const useQuestionsFeed = (params: { class_section_id?: string; exam_section_id?: string; exam_id?: string }) => useQuery({
  queryKey: ['feed', params],
  queryFn: () => {
    const qs = new URLSearchParams();
    if (params.class_section_id) qs.set('class_section_id', params.class_section_id);
    if (params.exam_section_id)  qs.set('exam_section_id', params.exam_section_id);
    if (params.exam_id)          qs.set('exam_id', params.exam_id);
    return authedFetch<{ questions: ApiQuestion[]; currentUserId: string; answeredQuestionIds?: string[]; wrongQuestionIds?: string[]; entitlements?: { canSeeProExplanation: boolean; canUseFlashcards: boolean; canUseFriendDecks: boolean } }>(`/questions/feed?${qs}`);
  },
  enabled: Boolean(params.class_section_id || params.exam_section_id || params.exam_id),
});

export const useClassUploads = (params: { class_section_id?: string; exam_section_id?: string }) => useQuery({
  queryKey: ['uploads-list', params],
  queryFn: () => {
    const qs = new URLSearchParams();
    if (params.class_section_id) qs.set('class_section_id', params.class_section_id);
    if (params.exam_section_id) qs.set('exam_section_id', params.exam_section_id);
    return authedFetch<{ uploads: UploadSource[] }>(`/materials/uploads?${qs}`);
  },
  enabled: Boolean(params.class_section_id || params.exam_section_id),
});

export const useClassFriendUploads = (params: { class_section_id?: string; exam_section_id?: string }) => useQuery({
  queryKey: ['friend-uploads-list', params],
  queryFn: () => {
    const qs = new URLSearchParams();
    if (params.class_section_id) qs.set('class_section_id', params.class_section_id);
    if (params.exam_section_id) qs.set('exam_section_id', params.exam_section_id);
    return authedFetch<{ uploads: UploadSource[] }>(`/materials/friend-uploads?${qs}`);
  },
  enabled: Boolean(params.class_section_id || params.exam_section_id),
});

export const useSubmitAttempt = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AttemptInput) =>
      authedFetch<AttemptResult>('/attempts', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: (_, input) => {
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['sessions'] });
      if (input.studySessionId) qc.invalidateQueries({ queryKey: ['session', input.studySessionId] });
    },
  });
};

export const useQuestionDetail = (questionId: string | null) => useQuery({
  queryKey: ['question-detail', questionId],
  queryFn: () => authedFetch<{ question: ApiQuestion }>(`/questions/${questionId}`),
  enabled: Boolean(questionId),
});

// ── Question Comments ────────────────────────────────────────────────────────

export interface QuestionComment {
  id: string;
  questionId: string;
  userId: string;
  body: string;
  upvoteCount: number;
  downvoteCount: number;
  score: number;
  createdAt: string;
  author: { displayName: string; avatarUrl: string | null };
  isOwn: boolean;
  myVote: number;
}

export const useQuestionComments = (questionId: string | null) => useQuery({
  queryKey: ['question-comments', questionId],
  queryFn: () => authedFetch<{
    comments: QuestionComment[];
    topPreview?: QuestionComment[];
    totalCount?: number;
    entitlements?: { canPost: boolean; canVote: boolean; canSeeAll: boolean };
  }>(`/question-comments?question_id=${questionId}`),
  enabled: Boolean(questionId),
});

export const usePostQuestionComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { questionId: string; body: string }) =>
      authedFetch<{ comment: any }>('/question-comments', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['question-comments', vars.questionId] }),
  });
};

export const useDeleteQuestionComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => authedFetch(`/question-comments/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-comments'] }),
  });
};

export const useVoteComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, value }: { commentId: string; value: number }) =>
      authedFetch<{ upvoteCount: number; downvoteCount: number; score: number; myVote: number }>(
        `/question-comments/${commentId}/vote`, { method: 'POST', body: JSON.stringify({ value }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-comments'] }),
  });
};

export const useReportComment = () => useMutation({
  mutationFn: ({ commentId, reason, details }: { commentId: string; reason: string; details?: string }) =>
    authedFetch(`/question-comments/${commentId}/report`, { method: 'POST', body: JSON.stringify({ reason, details }) }),
});

// ── Leaderboards ─────────────────────────────────────────────────────────────

export const useLeaderboard = (scope: LeaderboardScope, scopeId: string | null = null) => useQuery({
  queryKey: ['leaderboard', scope, scopeId],
  queryFn: () => {
    const qs = scopeId ? `?scope_id=${scopeId}` : '';
    return authedFetch<LeaderboardResponse>(`/leaderboards/${scope}${qs}`);
  },
  // Keep the friends ranking (and the remove trash) live as friends are
  // accepted/removed; also refreshes other rankings periodically.
  refetchInterval: 30_000,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
});

export const useGroupedLeaderboard = (scope: 'class' | 'school' | 'exam') => useQuery({
  queryKey: ['leaderboard', 'grouped', scope],
  queryFn: () => authedFetch<GroupedLeaderboardResponse>(`/leaderboards/grouped/${scope}`),
});

// ── Ingestion ────────────────────────────────────────────────────────────────

export const useStartIngestion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: IngestionRequest) =>
      authedFetch<{ job: IngestionJob }>('/ingest', { method: 'POST', body: JSON.stringify(req) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['materials'] }),
  });
};

// ── Reusable source material (Add-screen history) ────────────────────────────
export const useMaterials = () => useQuery({
  queryKey: ['materials'],
  queryFn: () => authedFetch<{ materials: StudyMaterial[] }>('/materials'),
});

export const useDeleteMaterial = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => authedFetch(`/materials/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['materials'] }),
  });
};

export const useIngestionJob = (jobId: string | null) => useQuery({
  queryKey: ['job', jobId],
  queryFn: () => authedFetch<{ job: IngestionJob }>(`/ingest/jobs/${jobId}`),
  enabled: Boolean(jobId),
  refetchInterval: (q) => {
    const j = (q.state.data as any)?.job;
    return j && (j.status === 'complete' || j.status === 'failed') ? false : 2000;
  },
});

export const usePresignUpload = () => useMutation({
  mutationFn: (body: { filename: string; mimeType: string; sizeBytes: number }) =>
    authedFetch<{ uploadId: string; url: string; key: string; bucket: string }>(
      '/uploads/presign', { method: 'POST', body: JSON.stringify(body) },
    ),
});

// ── Decks ────────────────────────────────────────────────────────────────────

export const useDecks = () => useQuery({
  queryKey: ['decks'],
  queryFn: () => authedFetch<{ decks: ApiDeck[] }>('/decks'),
});

// ── Billing ─────────────────────────────────────────────────────────────────

export const useSubscription = () => useQuery({
  queryKey: ['subscription'],
  queryFn: () => authedFetch<SubscriptionInfo>('/billing/subscription'),
});

export const useBillingConfig = () => useQuery({
  queryKey: ['billing-config'],
  queryFn: () => authedFetch<BillingConfigResponse>('/billing/config'),
});

export const useCheckout = () => useMutation({
  mutationFn: (req: CheckoutRequest) =>
    authedFetch<CheckoutResponse>('/billing/checkout', { method: 'POST', body: JSON.stringify(req) }),
});

export const usePortal = () => useMutation({
  mutationFn: () => authedFetch<{ url: string }>('/billing/portal', { method: 'POST', body: '{}' }),
});

// ── Favorites ───────────────────────────────────────────────────────────────

export const useFavorites = () => useQuery({
  queryKey: ['favorites'],
  queryFn: () => authedFetch<{ favorites: any[] }>('/favorites'),
});

// ── Friends ─────────────────────────────────────────────────────────────────

export interface FriendRow {
  id: string;
  first_name: string;
  last_name: string;
  display_name_mode: 'real_name' | 'custom' | 'anonymous';
  custom_handle: string | null;
  avatar_s3_key: string | null;
  status: 'pending' | 'accepted' | 'blocked';
  direction: 'incoming' | 'outgoing';
  requested_at: string;
  accepted_at: string | null;
}

export const useFriends = () => useQuery({
  queryKey: ['friends'],
  queryFn: () => authedFetch<{ friends: FriendRow[] }>('/friends'),
  // Near-live: pick up incoming requests + acceptances without a manual refresh.
  refetchInterval: 15_000,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
});

export const useInviteFriend = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email?: string; handle?: string; userId?: string }) =>
      authedFetch<{ status: string }>('/friends/invite', {
        method: 'POST', body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['friends'] }),
  });
};

export const useAcceptFriend = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (friendId: string) =>
      authedFetch(`/friends/accept/${friendId}`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['friends'] });
      qc.invalidateQueries({ queryKey: ['leaderboard'] }); // newly-accepted friend joins the ranking
    },
  });
};

export const useRemoveFriend = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (friendId: string) =>
      authedFetch(`/friends/${friendId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['friends'] });
      qc.invalidateQueries({ queryKey: ['leaderboard'] }); // removed friend leaves the Friends ranking
    },
  });
};

// ── Bug Reports ──────────────────────────────────────────────────────────────

export const useSubmitBugReport = () => useMutation({
  mutationFn: (body: { category: string; description: string }) =>
    authedFetch('/reports/bug', { method: 'POST', body: JSON.stringify(body) }),
});

export const useReportQuestion = () => useMutation({
  mutationFn: (body: { questionId: string; reason: string; details?: string }) =>
    authedFetch<{ reported: boolean; duplicate?: boolean }>('/reports', { method: 'POST', body: JSON.stringify(body) }),
});

// ── Comments ────────────────────────────────────────────────────────────

export const useClassComments = (classSectionId: string | undefined) => useQuery({
  queryKey: ['comments', classSectionId],
  queryFn: () => authedFetch<{
    comments: ClassComment[];
    entitlements?: { canPost: boolean; canReply: boolean; canSeeReplies: boolean };
  }>(`/comments?class_section_id=${classSectionId}`),
  enabled: Boolean(classSectionId),
});

export const usePostComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { classSectionId: string; parentId?: string | null; body: string }) =>
      authedFetch<{ comment: any }>('/comments', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['comments', vars.classSectionId] }),
  });
};

export const useDeleteComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, classSectionId }: { id: string; classSectionId: string }) =>
      authedFetch(`/comments/${id}`, { method: 'DELETE' }),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['comments', vars.classSectionId] }),
  });
};

// ── Avatar ──────────────────────────────────────────────────────────────────

export const useAvatarPresign = () => useMutation({
  mutationFn: () => authedFetch<{ url: string; key: string; bucket: string }>('/me/avatar', {
    method: 'POST', body: '{}',
  }),
});

// ── Taxonomy create ─────────────────────────────────────────────────────────

export const useCreateSchool = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; city?: string; state?: string; country?: string; domain?: string }) =>
      authedFetch<{ school: School }>('/taxonomy/schools', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schools'] }),
  });
};
export const useCreateClass = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { schoolId: string; code: string; name?: string }) =>
      authedFetch<{ class: ClassCode }>('/taxonomy/classes', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['classes'] }),
  });
};
export const useCreateProfessor = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { schoolId: string; name: string }) =>
      authedFetch<{ professor: Professor }>('/taxonomy/professors', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['professors'] }),
  });
};
export const useCreateSection = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { classCodeId: string; professorId: string | null; term: string | null; examDate?: string | null }) =>
      authedFetch<{ section: ClassSection }>('/taxonomy/sections', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sections'] }),
  });
};
export const useEnrollSection = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (classSectionId: string) =>
      authedFetch('/taxonomy/enroll', { method: 'POST', body: JSON.stringify({ classSectionId }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['enrollments'] }),
  });
};
export const useUnenrollSection = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (classSectionId: string) =>
      authedFetch(`/taxonomy/enroll/${classSectionId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['enrollments'] }),
  });
};

export const usePopularClasses = (schoolId: string | null) => useQuery({
  queryKey: ['popular-classes', schoolId],
  queryFn: () => authedFetch<{ classes: ClassCode[] }>(`/taxonomy/classes/popular?school_id=${schoolId}`),
  enabled: Boolean(schoolId),
});

// ── Announcements ────────────────────────────────────────────────────────────

export interface Announcement {
  id: string;
  title: string;
  body: string;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
}

export const useActiveAnnouncements = () => useQuery({
  queryKey: ['announcements', 'active'],
  queryFn: () => authedFetch<{ announcements: Announcement[] }>('/announcements/active'),
  refetchInterval: 30_000,
  refetchOnWindowFocus: true,
  refetchIntervalInBackground: true,
  staleTime: 0,
});

export const useDismissAnnouncement = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authedFetch(`/announcements/${id}/dismiss`, { method: 'POST', body: '{}' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements', 'active'] }),
  });
};

// ── Social Sharing ──────────────────────────────────────────────────────────

export const useCreateShare = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { question_id: string; share_type?: string }) =>
      authedFetch<{ share_id: string; public_url: string }>('/shares', {
        method: 'POST', body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['share-stats'] });
    },
  });
};

export const useShareStats = () => useQuery({
  queryKey: ['share-stats'],
  queryFn: () => authedFetch<{
    total_shares: number;
    shared_today: number;
    total_answers: number;
    total_clicks: number;
    total_signups: number;
    correct_percentage: number;
  }>('/shares/stats'),
});

export const useReferralInfo = () => useQuery({
  queryKey: ['referral'],
  queryFn: () => authedFetch<{
    referral_url: string;
    balance: number;
    referrals: Array<{
      id: string;
      first_name: string;
      last_name: string;
      created_at: string;
      subscription_tier: string;
      event_type: string | null;
      points_awarded: number | null;
    }>;
  }>('/referral'),
});

// Critical thinking content
export const useCriticalThinkingItems = (discipline?: string, category?: string) => useQuery({
  queryKey: ['critical-thinking', discipline ?? 'nursing', category ?? 'all'],
  queryFn: () => {
    const qs = new URLSearchParams();
    if (discipline) qs.set('discipline', discipline);
    if (category) qs.set('category', category);
    const s = qs.toString();
    return authedFetch<{ items: CriticalThinkingListItem[] }>(`/critical-thinking${s ? `?${s}` : ''}`);
  },
});

export const useCriticalThinkingItem = (id: string | null) => useQuery({
  queryKey: ['critical-thinking-item', id],
  queryFn: () => authedFetch<{ item: CriticalThinkingFullItem }>(`/critical-thinking/${id}`),
  enabled: !!id,
});

export interface CriticalThinkingListItem {
  id: string;
  externalId: string;
  category: string;
  title: string;
  topic: string;
  difficulty: number;
  clinicalSetting: string | null;
  tags: string[] | null;
}

export interface CriticalThinkingFullItem extends CriticalThinkingListItem {
  content: Record<string, any>;
  sourceInspiration: any;
}

// Public share page API (no auth)
export async function fetchPublicQuestion(shareId: string) {
  return publicFetch<{
    share_id: string;
    question_type: string;
    question: string;
    options?: string[];
    blanks_count?: number;
    topic?: string;
    subtopic?: string;
    exam_name?: string;
    exam_code?: string;
    has_explanation: boolean;
    // Flashcard fields
    front?: string;
    back?: string;
    category?: string;
  }>(`/q/${shareId}`);
}

export async function submitShareAnswer(
  shareId: string,
  answer: { chosen_index: number | number[] } | { text_answers: string[] },
  fingerprint: string,
) {
  return publicFetch<{
    correct: boolean;
    correct_index?: number | number[];
    correct_blanks?: string[];
    brief_explanation: string | null;
    already_answered: boolean;
  }>(`/q/${shareId}/answer`, {
    method: 'POST',
    body: JSON.stringify({ ...answer, fingerprint }),
  });
}

// ── Study Groups ────────────────────────────────────────────────────────────

export interface StudyGroupListItem {
  id: string;
  roomCode: string;
  title: string;
  sourceType: string;
  sourceId: string;
  status: 'lobby' | 'countdown' | 'active' | 'completed' | 'expired';
  questionCount: number;
  timerSeconds: number;
  memberCap: number;
  totalQuestions: number;
  memberCount: number;
  creatorUserId: string;
  creatorDisplayName: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface StudyGroupSource {
  id: string;
  name: string;
  mcqCount: number;
}

export interface StudyGroupTopic {
  name: string;
  mcqCount: number;
}

export interface StudyGroupMemberDetail {
  id: string;
  userId: string;
  status: string;
  joinedAt: string;
  totalPoints: number;
  correctCount: number;
  currentStreak: number;
  longestStreak: number;
  displayName: string;
  avatarUrl: string | null;
  isCreator: boolean;
}

export interface StudyGroupStanding {
  rank: number;
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  totalPoints: number;
  correctCount: number;
  accuracy: number;
  longestStreak: number;
  avgResponseMs: number | null;
  status: string;
}

export interface StudyGroupTopicBreakdown {
  topic: string;
  totalQuestions: number;
  userAccuracy: number;
  groupAccuracy: number;
}

export function useStudyGroups() {
  return useQuery({
    queryKey: ['study-groups'],
    queryFn: () => authedFetch<{ groups: StudyGroupListItem[] }>('/study-groups'),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });
}

export function useStudyGroup(id: string | null) {
  return useQuery({
    queryKey: ['study-group', id],
    queryFn: () => authedFetch<{ group: any; members: StudyGroupMemberDetail[] }>(`/study-groups/${id}`),
    enabled: !!id,
    refetchInterval: 5_000,
  });
}

export function useStudyGroupSources() {
  return useQuery({
    queryKey: ['study-group-sources'],
    queryFn: () => authedFetch<{ classes: StudyGroupSource[]; exams: StudyGroupSource[] }>('/study-groups/sources'),
  });
}

export function useStudyGroupTopics(sourceType: string | null, sourceId: string | null) {
  return useQuery({
    queryKey: ['study-group-topics', sourceType, sourceId],
    queryFn: () => authedFetch<{ topics: StudyGroupTopic[]; totalMcqCount: number }>(
      `/study-groups/sources/${sourceType}/${sourceId}/topics`,
    ),
    enabled: !!sourceType && !!sourceId,
  });
}

export function useCreateStudyGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      sourceType: string;
      sourceId: string;
      topicFilter: string[] | null;
      questionCount: number;
      timerSeconds: number;
      questionType?: 'mcq' | 'quick_mcq';
      candidateQuestionIds?: string[];
    }) => authedFetch<{ group: any; inviteLink: string }>('/study-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['study-groups'] }),
  });
}

export function useValidateRoomCode(roomCode: string | null) {
  return useQuery({
    queryKey: ['study-group-validate', roomCode],
    queryFn: () => authedFetch<{ preview: {
      id: string;
      title: string;
      sourceName: string;
      sourceType: string;
      questionCount: number;
      timerSeconds: number;
      memberCount: number;
      memberCap: number;
      status: string;
      createdAt: string;
    } }>(`/study-groups/join/${roomCode}`),
    enabled: !!roomCode,
    retry: false,
  });
}

export function useJoinStudyGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roomCode: string) => authedFetch<{ group: { id: string } }>(
      `/study-groups/join/${roomCode}`,
      { method: 'POST' },
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['study-groups'] }),
  });
}

export function useLeaveStudyGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => authedFetch(`/study-groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['study-groups'] }),
  });
}

export function useStudyGroupResults(id: string | null) {
  return useQuery({
    queryKey: ['study-group-results', id],
    queryFn: () => authedFetch<{
      standings: StudyGroupStanding[];
      awards: Record<string, any>;
      topicBreakdown: StudyGroupTopicBreakdown[];
      totalQuestions: number;
    }>(`/study-groups/${id}/results`),
    enabled: !!id,
  });
}

export function useStudyGroupQuestions(id: string | null) {
  return useQuery({
    queryKey: ['study-group-questions', id],
    queryFn: () => authedFetch<{ questions: any[]; questionIds: string[] }>(`/study-groups/${id}/questions`),
    enabled: !!id,
  });
}

// ── Notifications ───────────────────────────────────────────────────────────

export function useNotifications(limit = 20) {
  return useQuery({
    queryKey: ['notifications', limit],
    queryFn: () => authedFetch<{ notifications: any[]; total: number }>(
      `/notifications?limit=${limit}`,
    ),
    refetchInterval: 25_000,        // keep the bell + invite popup fresh
    refetchOnWindowFocus: true,
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => authedFetch<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => authedFetch(`/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authedFetch('/notifications/read-all', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
}

// ── Login Rewards ─────────────────────────────────────────────────────────────

export const useLoginRewardStatus = () => useQuery({
  queryKey: ['login-rewards'],
  queryFn: () => authedFetch<{
    currentDay: number;
    claimedToday: boolean;
    nextReward: number;
    rewards: number[];
    proMultiplier: boolean;
    history: { reward_day: number; streak_date: string; xp_awarded: number }[];
  }>('/login-rewards'),
});

export const useClaimLoginReward = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authedFetch<{
      claimed: boolean;
      rewardDay: number;
      xpAwarded: number;
      baseReward: number;
      proBonus: number;
    }>('/login-rewards/claim', { method: 'POST', body: '{}' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['login-rewards'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
};

export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

// ─── Professor / TA ─────────────────────────────────────────────────────────

export interface ProfessorSection {
  class_section_id: string;
  term: string | null;
  class_code: string;
  class_name: string | null;
  school_id: string;
  school_name: string;
  professor_name: string | null;
}

export interface ProfessorStatus {
  isProfessor: boolean;
  canTeach: boolean;
  records: Array<{ id: string; name: string; verified: boolean; title: string | null; schoolId: string }>;
}

export function useProfessorStatus() {
  return useQuery({
    queryKey: ['professor', 'me'],
    queryFn: () => authedFetch<ProfessorStatus>('/professor/me'),
    staleTime: 60_000,
  });
}

/** Only the classes and terms an admin granted this professor. */
export function useProfessorSections(enabled = true) {
  return useQuery({
    queryKey: ['professor', 'sections'],
    queryFn: () => authedFetch<{ sections: ProfessorSection[] }>('/professor/sections'),
    enabled,
    staleTime: 60_000,
  });
}

/** "Write it for me": returns both tiers for review, does NOT save. */
export function useGenerateExplanation() {
  return useMutation({
    mutationFn: (body: {
      type: 'mcq' | 'sata'; question: string; options: string[];
      correct: number | number[]; context?: string;
    }) => authedFetch<{ explanation: string; proExplanation: string }>(
      '/professor/explain', { method: 'POST', body: JSON.stringify(body) }),
  });
}

export function useCreateProfessorQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      classSectionId: string; type: 'mcq' | 'sata'; question: string;
      options: string[]; correct: number | number[];
      explanation?: string; proExplanation?: string;
      explanationSource: 'human' | 'ai' | 'ai_edited';
      difficulty?: number; topic?: string; subtopic?: string;
    }) => authedFetch('/professor/questions', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feed'] }),
  });
}

export interface InviteCode {
  id: string; code: string; class_section_id: string; label: string | null;
  expires_at: string | null; max_uses: number | null; uses: number; active: boolean;
  school_name: string; class_code: string; term: string | null; redemptions: number;
}

export function useInviteCodes(enabled = true) {
  return useQuery({
    queryKey: ['professor', 'invite-codes'],
    queryFn: () => authedFetch<{ inviteCodes: InviteCode[] }>('/professor/invite-codes'),
    enabled,
  });
}

export function useCreateInviteCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { classSectionId: string; label?: string; maxUses?: number; expiresAt?: string }) =>
      authedFetch<{ inviteCode: InviteCode }>('/professor/invite-codes', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['professor', 'invite-codes'] }),
  });
}

export function useSetInviteCodeActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      authedFetch(`/professor/invite-codes/${id}`, { method: 'PATCH', body: JSON.stringify({ active }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['professor', 'invite-codes'] }),
  });
}

// ─── Invite redemption (student side) ───────────────────────────────────────

export interface InvitePreview {
  code: string; classSectionId: string; schoolName: string;
  classCode: string; className: string | null; term: string | null; professorName: string | null;
}

/** Preview what a code joins, so the student confirms before enrolling. */
export function useInvitePreview(code: string | null) {
  return useQuery({
    queryKey: ['invite', code],
    queryFn: () => authedFetch<{ invite: InvitePreview }>(`/invite/${encodeURIComponent(code!)}`),
    enabled: Boolean(code && code.length >= 4),
    retry: false,
  });
}

export function useRedeemInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      authedFetch<{ enrolled: boolean; alreadyMember: boolean; classSectionId: string; classCode: string }>(
        `/invite/${encodeURIComponent(code)}/redeem`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
